import { h } from "preact";
import { useEffect } from "preact/hooks";

import { Keyboard } from "../../../foundation/enums";
import IconCloseOutline from "../Icons/IconCloseOutline";
import IconOpenInFigma from "../Icons/IconOpenInFigma";
import Portal from "../../Portal";
import OnlyIconButton, { OnlyIconButtonVariant } from "../OnlyIconButton";

import styles from "./EmbedDialog.module.css";

enum SourceType {
    FigmaPrototype = "figmaPrototype",
    YouTube = "youtube"
}

type SourceFigma = {
    type: SourceType.FigmaPrototype;
    url: string;
};

type SourceYouTube = {
    type: SourceType.YouTube;
    videoId: string;
};

type Source = SourceFigma | SourceYouTube;

function getURLFromSource(source: Source) {
    switch (source.type) {
        case SourceType.FigmaPrototype: {
            const figmaPrototypeURL = new URL(source.url);
            // Force "Fit to screen".
            figmaPrototypeURL.searchParams.set("scaling", "scale-down");
            // Do not show Figma's sidebar to save space and prevent users to navigate to other prototypes.
            figmaPrototypeURL.searchParams.delete("show-proto-sidebar");

            return `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(figmaPrototypeURL.href)}`;
        }

        case SourceType.YouTube: {
            return `https://www.youtube.com/embed/${source.videoId}?autoplay=1`;
        }

        default: {
            throw new Error(`Rendering \`EmbedDialog\` failed with source \`${source}\``);
        }
    }
}

interface Props {
    source: Source;
    onClose?: () => void;
}

export default function EmbedDialog({
    source,
    onClose
}: Props) {
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.code === Keyboard.Key.Escape) {
            event.stopPropagation();
            onClose?.();
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown, true);

        return () => document.removeEventListener("keydown", handleKeyDown, true);
    }, []);

    function handleClose(event: Event) {
        event.stopPropagation();

        onClose?.();
    }

    function handleOpenInFigma() {
        window.open((source as SourceFigma).url, "_blank");
    }

    return (
        <Portal>
            <div class={styles.embedDialogContainer}>
                <div
                    class={styles.backdrop}
                    onClick={handleClose}/>
                <div class={styles.embedDialog}>
                    <iframe
                        class={styles.content}
                        src={getURLFromSource(source)}
                        allowFullScreen
                        /* @ts-ignore - Attribute not defined in current version of TypeScript. */
                        allow="autoplay"/>
                    <div class={styles.controls}>
                        <OnlyIconButton
                            icon={IconCloseOutline}
                            variant={OnlyIconButtonVariant.Ninja}
                            onClick={handleClose}/>
                        {source.type === SourceType.FigmaPrototype && (
                            <OnlyIconButton
                                icon={IconOpenInFigma}
                                variant={OnlyIconButtonVariant.Ninja}
                                onClick={handleOpenInFigma}/>
                        )}
                    </div>
                </div>
            </div>
        </Portal>
    );
}

export {
    Source as EmbedDialogSource,
    SourceType as EmbedDialogSourceType
};
